import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, withCtx as _withCtx, createVNode as _createVNode, normalizeClass as _normalizeClass, createElementBlock as _createElementBlock, toDisplayString as _toDisplayString, vShow as _vShow, createElementVNode as _createElementVNode, withDirectives as _withDirectives, Fragment as _Fragment, createTextVNode as _createTextVNode, renderList as _renderList, normalizeStyle as _normalizeStyle, renderSlot as _renderSlot } from "vue"

const _hoisted_1 = { class: "header-icon-container" }
const _hoisted_2 = { key: 2 }
const _hoisted_3 = { class: "header-module-text" }
const _hoisted_4 = { class: "header-actions" }
const _hoisted_5 = {
  key: 0,
  class: "golf-header-search"
}
const _hoisted_6 = {
  key: 0,
  class: "notif-dot"
}
const _hoisted_7 = {
  key: 2,
  class: "golf-lang-selector-wrapper"
}
const _hoisted_8 = { class: "golf-lang-btn" }
const _hoisted_9 = { class: "golf-lang-code" }
const _hoisted_10 = {
  key: 3,
  class: "golf-header-profile"
}
const _hoisted_11 = { class: "profile-info" }
const _hoisted_12 = { class: "profile-name" }
const _hoisted_13 = { class: "profile-role" }
const _hoisted_14 = {
  key: 0,
  class: "golf-body-wrapper"
}

import { computed, ref, watch, onMounted, onBeforeUnmount, nextTick } from 'vue'
import { DateTime } from 'luxon'
import { Fold, Expand } from '@element-plus/icons'
import { Menu as MenuIcon, Bell, Search, ArrowDown } from '@element-plus/icons-vue'
import { useRouter, useRoute } from 'vue-router'
import { getGolfDashboard } from '@/api/golf'

import { useAuthStore } from '@/store/auth'
import { LsGlobalKeys, lsGet, lsSave, lsRemove } from '@/helpers/local-storage'
import { getDefaultPaginationQuery } from '@/constants/pagination'
import { routeNames } from '@/router/route-names'
import {
  hasEvAccess as checkEvAccess,
  hasGolfAccess as checkGolfAccess,
  hasP2pAccess as checkP2pAccess,
} from '@/helpers/rbac'

import MainMenu from './MainMenu.vue'
import EVMenu from './EVMenu.vue'
import GolfMenu from './GolfMenu.vue'
import { useGolfHeaderStore } from '@/store/golf-header'
import { useGolfI18n } from '@/composables/useGolfI18n'
import type { GolfLanguage } from '@/store/golf-i18n'


export default /*@__PURE__*/_defineComponent({
  __name: 'index',
  setup(__props) {

const authStore = useAuthStore()
const headerStore = useGolfHeaderStore()
const router = useRouter()
const route = useRoute()

const {
  currentLanguage: currentGolfLanguage,
  currentLanguageOption: currentGolfLanguageOption,
  languages: golfLanguages,
  setLanguage: setGolfLanguage,
  fetchTranslations: fetchGolfTranslations,
  t,
} = useGolfI18n()

const TITLE_TO_KEY: Record<string, string> = {
  'Fleet Management': 'golf.fleet.title',
  'Dashboard': 'golf.dashboard.title',
  'Trips': 'golf.trips.title',
  'Reserves': 'golf.reserves.title',
  'Map': 'golf.map.title',
  'Resort Map': 'golf.map.title',
  'Tariff & Settings': 'golf.tariffs.title',
  'Financial Reports & Billing': 'golf.financial.title',
  'Users & Access Management': 'golf.roles.title',
  'Resort Deployment Zone': 'golf.zones.title',
  'Users': 'golf.users.title',
  'Promo Codes': 'golf.promo.title',
}

const displayedGolfTitle = computed(() => {
  const raw = headerStore.title
  if (!raw) return ''
  if (raw.startsWith('golf.')) return t(raw)
  const mappedKey = TITLE_TO_KEY[raw]
  if (mappedKey) return t(mappedKey)
  return raw
})

const displayedGolfSearchPlaceholder = computed(() => {
  const raw = headerStore.searchPlaceholder
  if (!raw) return ''
  if (raw.startsWith('golf.')) return t(raw)
  return t('golf.header.search_placeholder') || raw
})

const handleGolfLanguageChange = (lang: string) => {
  setGolfLanguage(lang as GolfLanguage)
}

// Clean up any stale portal children whenever route changes
watch(
  () => route.path,
  () => {
    if (typeof document !== 'undefined') {
      const portal = document.getElementById('golf-header-portal')
      if (portal) {
        portal.innerHTML = ''
      }
    }
  }
)

const collapse = ref(false)
const drawerVisible = ref(false)

// Initialize currentModule from localStorage or default to 'p2p'
const getSavedModule = (): 'p2p' | 'ev' | 'golf' => {
  const savedModuleRaw = lsGet(LsGlobalKeys.CURRENT_MODULE)

  let savedModule: string | null = null
  if (savedModuleRaw) {
    try {
      // Try to parse as JSON first
      savedModule = JSON.parse(savedModuleRaw)
    } catch {
      // If parsing fails, use as string
      savedModule = savedModuleRaw
    }
  }

  const result =
    savedModule === 'ev' || savedModule === 'golf'
      ? (savedModule as 'ev' | 'golf')
      : 'p2p'
  return result
}

// Check if current user has access to each module
const hasGolfAccess = computed(() => checkGolfAccess(authStore.session?.user))
const hasEvAccess = computed(() => checkEvAccess(authStore.session?.user))
const hasP2pAccess = computed(() => checkP2pAccess(authStore.session?.user))

const userName = computed(() => {
  const user = authStore.session?.user as any
  if (!user) return 'Admin'

  if (user.first_name || user.last_name) {
    return `${user.first_name || ''} ${user.last_name || ''}`.trim()
  }

  return user.email || user.name || 'Admin'
})

const userRole = computed(() => {
  const user = authStore.session?.user
  if (!user) return 'ADMINISTRATOR'
  const role = user.golf_role || user.role || 'ADMINISTRATOR'
  return role.replace(/_/g, ' ').toUpperCase()
})

// Initialize with default value, then update when session is available
const currentModule = ref<'p2p' | 'ev' | 'golf'>(getSavedModule())

// Watch for session changes and update module accordingly
watch(
  () => authStore.session?.user,
  (newUser: any) => {
    if (newUser) {
      let module = getSavedModule()
      if (module === 'ev' && !hasEvAccess.value) {
        module = hasP2pAccess.value
          ? 'p2p'
          : hasGolfAccess.value
          ? 'golf'
          : 'p2p'
      } else if (module === 'golf' && !hasGolfAccess.value) {
        module = hasP2pAccess.value
          ? 'p2p'
          : hasEvAccess.value
          ? 'ev'
          : 'p2p'
      } else if (module === 'p2p' && !hasP2pAccess.value) {
        module = hasEvAccess.value
          ? 'ev'
          : hasGolfAccess.value
          ? 'golf'
          : 'p2p'
      }
      currentModule.value = module
      lsSave(LsGlobalKeys.CURRENT_MODULE, module)
    }
  },
  { immediate: true }
)

watch(
  currentModule,
  val => {
    if (val === 'golf') {
      fetchGolfTranslations()
    }
  },
  { immediate: true }
)


// Watch for route changes and save current route
watch(
  () => router.currentRoute.value,
  newRoute => {
    // Save current route to localStorage
    lsSave(LsGlobalKeys.LAST_ROUTE, {
      name: newRoute.name,
      params: newRoute.params,
      query: newRoute.query,
    })
    drawerVisible.value = false
  },
  { immediate: true }
)

const switchToModuleAndClose = (module: string) => {
  switchToModule(module)
  drawerVisible.value = false
}

const switchToModule = async (module: string) => {
  collapse.value = false
  const newModule = module as 'p2p' | 'ev' | 'golf'
  const currentUser = authStore.session?.user

  if (newModule === 'ev' && !checkEvAccess(currentUser)) return
  if (newModule === 'golf' && !checkGolfAccess(currentUser)) return
  if (newModule === 'p2p' && !checkP2pAccess(currentUser)) return

  currentModule.value = newModule
  lsSave(LsGlobalKeys.CURRENT_MODULE, newModule)

  try {
    if (newModule === 'ev') {
      await router.push({
        name: routeNames.evUsers,
        query: getDefaultPaginationQuery(),
      })
    } else if (newModule === 'golf') {
      await router.push({
        name: routeNames.golfCars,
        query: getDefaultPaginationQuery(),
      })
    } else {
      await router.push({
        name: routeNames.users,
        query: getDefaultPaginationQuery(),
      })
    }
  } catch (error) {
    console.error('Navigation failed during module switch:', error)
  }
}

const signOut = () => {
  authStore.signOut()
}

// ─── Pending Requests Bell ───────────────────────────────────────────
const pendingCount = ref(0)
let pendingPollInterval: ReturnType<typeof setInterval> | null = null

const fetchPendingCount = async () => {
  if (currentModule.value !== 'golf') return
  try {
    const res = await getGolfDashboard()
    const responseData = res.data?.data as any
    const stats = responseData?.data ?? responseData
    pendingCount.value = stats?.pendingTrips ?? 0
  } catch {
    // silently ignore — network errors should not disrupt the header
  }
}

const goToPendingTrips = () => {
  router.push({
    name: routeNames.golfTrips,
    query: { status: 'pending', page: '1', per_page: '20' },
  })
}

const startPendingPoll = () => {
  fetchPendingCount()
  pendingPollInterval = setInterval(fetchPendingCount, 5000)
}

const stopPendingPoll = () => {
  if (pendingPollInterval !== null) {
    clearInterval(pendingPollInterval)
    pendingPollInterval = null
  }
}

// Start / stop poll when module changes
watch(
  () => currentModule.value,
  mod => {
    if (mod === 'golf') {
      startPendingPoll()
    } else {
      stopPendingPoll()
      pendingCount.value = 0
    }
  }
)

onBeforeUnmount(() => {
  stopPendingPoll()
})
// ─────────────────────────────────────────────────────────────────────

// Check if current user is statistics-viewer (restricted access)
const isStatisticsViewer = computed(() => {
  const currentUser = authStore.session?.user
  return (
    currentUser?.role === 'statistics-viewer' ||
    currentUser?.ev_role === 'statistics-viewer'
  )
})

// Check if current user is ev_car_owner (restricted access)
const isEvCarOwner = computed(() => {
  const currentUser = authStore.session?.user
  return currentUser?.ev_role === 'ev_car_owner'
})

// Calculate container height dynamically based on visible header/footer
const containerHeight = computed(() => {
  if (isStatisticsViewer.value || isEvCarOwner.value) {
    return '100vh'
  }
  return 'calc(100vh - 60px - 26px)'
})

const currentYear = computed(() => DateTime.local().year)

// Restore last route on component mount
onMounted(async () => {
  // Start pending poll immediately if already on golf module
  if (currentModule.value === 'golf') {
    startPendingPoll()
  }
  // Wait for the next tick to ensure all components are mounted
  await nextTick()

  const lastRoute = lsGet(LsGlobalKeys.LAST_ROUTE)
  const savedModuleRaw = lsGet(LsGlobalKeys.CURRENT_MODULE)

  let savedModule: string | null = null
  if (savedModuleRaw) {
    try {
      savedModule = JSON.parse(savedModuleRaw)
    } catch {
      savedModule = savedModuleRaw
    }
  }

  if (lastRoute && savedModule) {
    try {
      const routeData = JSON.parse(lastRoute)
      const currentUser = authStore.session?.user
      const routeName = (routeData?.name || '').toString().toLowerCase()
      const isEvRoute = routeName.startsWith('ev') || routeName.includes('evcar')
      const isGolfRoute = routeName.startsWith('golf')
      const isP2pRoute = !isEvRoute && !isGolfRoute

      if (isEvRoute && !checkEvAccess(currentUser)) {
        lsRemove(LsGlobalKeys.LAST_ROUTE)
        return
      }
      if (isGolfRoute && !checkGolfAccess(currentUser)) {
        lsRemove(LsGlobalKeys.LAST_ROUTE)
        return
      }
      if (isP2pRoute && !checkP2pAccess(currentUser)) {
        lsRemove(LsGlobalKeys.LAST_ROUTE)
        return
      }

      // Check if the saved module matches current module
      if (savedModule === currentModule.value) {
        router.push(routeData)
      }
    } catch (error) {
      console.error('❌ Failed to restore last route:', error)
    }
  } else {
    console.error('📭 No saved route or module found')
  }
})

return (_ctx: any,_cache: any) => {
  const _component_el_icon = _resolveComponent("el-icon")!
  const _component_el_input = _resolveComponent("el-input")!
  const _component_el_dropdown_item = _resolveComponent("el-dropdown-item")!
  const _component_el_dropdown_menu = _resolveComponent("el-dropdown-menu")!
  const _component_el_dropdown = _resolveComponent("el-dropdown")!
  const _component_el_avatar = _resolveComponent("el-avatar")!
  const _component_el_button = _resolveComponent("el-button")!
  const _component_el_header = _resolveComponent("el-header")!
  const _component_el_drawer = _resolveComponent("el-drawer")!
  const _component_el_main = _resolveComponent("el-main")!
  const _component_el_container = _resolveComponent("el-container")!
  const _component_el_footer = _resolveComponent("el-footer")!

  return (_openBlock(), _createBlock(_component_el_container, {
    direction: currentModule.value === 'golf' ? 'horizontal' : 'vertical',
    class: _normalizeClass(["root-layout", [
      currentModule.value === 'golf' ? 'golf-lang-' + _unref(currentGolfLanguage) : '',
      'module-' + currentModule.value,
    ]]),
    style: _normalizeStyle(currentModule.value === 'golf' ? 'height: 100vh; overflow: hidden;' : '')
  }, {
    default: _withCtx(() => [
      (
        currentModule.value === 'golf' &&
          !isStatisticsViewer.value &&
          !isEvCarOwner.value &&
          hasGolfAccess.value
      )
        ? (_openBlock(), _createBlock(GolfMenu, {
            key: 0,
            collapse: collapse.value,
            "show-golf-module": hasGolfAccess.value,
            onSwitchModule: switchToModule,
            class: "desktop-sidebar",
            style: {"height":"100vh"}
          }, null, 8, ["collapse", "show-golf-module"]))
        : _createCommentVNode("", true),
      _createVNode(_component_el_container, {
        direction: "vertical",
        class: "main-column",
        style: _normalizeStyle(
        currentModule.value === 'golf' ? 'height: 100vh; overflow: hidden;' : ''
      )
      }, {
        default: _withCtx(() => [
          (!isStatisticsViewer.value && !isEvCarOwner.value)
            ? (_openBlock(), _createBlock(_component_el_header, {
                key: 0,
                style: _normalizeStyle(currentModule.value === 'golf' ? 'background-color: #fafdfc;' : '')
              }, {
                default: _withCtx(() => [
                  _createElementVNode("div", _hoisted_1, [
                    (currentModule.value !== 'golf')
                      ? (_openBlock(), _createBlock(_component_el_icon, {
                          key: 0,
                          size: 24,
                          class: "desktop-only",
                          onClick: _cache[0] || (_cache[0] = ($event: any) => (collapse.value = !collapse.value))
                        }, {
                          default: _withCtx(() => [
                            (collapse.value)
                              ? (_openBlock(), _createBlock(_unref(Expand), { key: 0 }))
                              : (_openBlock(), _createBlock(_unref(Fold), { key: 1 }))
                          ]),
                          _: 1
                        }))
                      : _createCommentVNode("", true),
                    _createVNode(_component_el_icon, {
                      size: 24,
                      class: "mobile-only",
                      onClick: _cache[1] || (_cache[1] = ($event: any) => (drawerVisible.value = true))
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_unref(MenuIcon))
                      ]),
                      _: 1
                    }),
                    (currentModule.value === 'golf')
                      ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                          (_openBlock(), _createElementBlock("div", {
                            id: "golf-header-portal",
                            key: _unref(route).fullPath,
                            class: _normalizeClass({ 'has-content': _unref(headerStore).useCustomHeader })
                          }, null, 2)),
                          _withDirectives(_createElementVNode("h2", { class: "golf-header-title" }, _toDisplayString(displayedGolfTitle.value), 513), [
                            [_vShow, !_unref(headerStore).useCustomHeader]
                          ])
                        ], 64))
                      : (_openBlock(), _createElementBlock("p", _hoisted_2, [
                          _cache[4] || (_cache[4] = _createTextVNode(" Rentbutik admin panel")),
                          _createElementVNode("span", _hoisted_3, " - " + _toDisplayString(currentModule.value.toUpperCase()) + " Module", 1)
                        ]))
                  ]),
                  _createElementVNode("div", _hoisted_4, [
                    (currentModule.value === 'golf' && _unref(headerStore).showSearch)
                      ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
                          _createVNode(_component_el_input, {
                            modelValue: _unref(headerStore).searchQuery,
                            "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((_unref(headerStore).searchQuery) = $event)),
                            placeholder: displayedGolfSearchPlaceholder.value,
                            clearable: ""
                          }, {
                            prefix: _withCtx(() => _cache[5] || (_cache[5] = [
                              _createElementVNode("img", {
                                src: "/icons_golf_module/Search icon.svg",
                                alt: "Search",
                                class: "custom-search-icon"
                              }, null, -1)
                            ])),
                            _: 1
                          }, 8, ["modelValue", "placeholder"])
                        ]))
                      : _createCommentVNode("", true),
                    (currentModule.value === 'golf')
                      ? (_openBlock(), _createElementBlock("div", {
                          key: 1,
                          class: _normalizeClass(["notif-bell-wrapper", { 'has-pending': pendingCount.value > 0 }]),
                          onClick: goToPendingTrips,
                          title: "Pending trip requests"
                        }, [
                          _createVNode(_component_el_icon, {
                            size: 20,
                            class: "notif-bell-icon"
                          }, {
                            default: _withCtx(() => [
                              _createVNode(_unref(Bell))
                            ]),
                            _: 1
                          }),
                          (pendingCount.value > 0)
                            ? (_openBlock(), _createElementBlock("span", _hoisted_6))
                            : _createCommentVNode("", true)
                        ], 2))
                      : _createCommentVNode("", true),
                    (currentModule.value === 'golf')
                      ? (_openBlock(), _createElementBlock("div", _hoisted_7, [
                          _createVNode(_component_el_dropdown, {
                            trigger: "click",
                            onCommand: handleGolfLanguageChange
                          }, {
                            dropdown: _withCtx(() => [
                              _createVNode(_component_el_dropdown_menu, { class: "golf-lang-dropdown" }, {
                                default: _withCtx(() => [
                                  (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(golfLanguages), (lang) => {
                                    return (_openBlock(), _createBlock(_component_el_dropdown_item, {
                                      key: lang.code,
                                      command: lang.code,
                                      class: _normalizeClass({
                      'is-selected': _unref(currentGolfLanguage) === lang.code,
                    })
                                    }, {
                                      default: _withCtx(() => [
                                        _createTextVNode(_toDisplayString(lang.code.toUpperCase()), 1)
                                      ]),
                                      _: 2
                                    }, 1032, ["command", "class"]))
                                  }), 128))
                                ]),
                                _: 1
                              })
                            ]),
                            default: _withCtx(() => [
                              _createElementVNode("div", _hoisted_8, [
                                _createElementVNode("span", _hoisted_9, _toDisplayString(_unref(currentGolfLanguage).toUpperCase()), 1),
                                _createVNode(_component_el_icon, { class: "golf-lang-arrow" }, {
                                  default: _withCtx(() => [
                                    _createVNode(_unref(ArrowDown))
                                  ]),
                                  _: 1
                                })
                              ])
                            ]),
                            _: 1
                          })
                        ]))
                      : _createCommentVNode("", true),
                    (currentModule.value === 'golf')
                      ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
                          _createElementVNode("div", _hoisted_11, [
                            _createElementVNode("span", _hoisted_12, _toDisplayString(userName.value), 1),
                            _createElementVNode("span", _hoisted_13, _toDisplayString(userRole.value), 1)
                          ]),
                          _createVNode(_component_el_avatar, {
                            size: 32,
                            src: "https://cube.elemecdn.com/3/7c/3ea6beec64369c2642b92c6726f1epng.png",
                            class: "profile-avatar"
                          })
                        ]))
                      : (_openBlock(), _createBlock(_component_el_button, {
                          key: 4,
                          class: "signout-btn",
                          type: "text",
                          size: "medium",
                          onClick: signOut
                        }, {
                          default: _withCtx(() => _cache[6] || (_cache[6] = [
                            _createTextVNode("Sign out")
                          ])),
                          _: 1,
                          __: [6]
                        }))
                  ])
                ]),
                _: 1
              }, 8, ["style"]))
            : _createCommentVNode("", true),
          _createVNode(_component_el_drawer, {
            modelValue: drawerVisible.value,
            "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((drawerVisible).value = $event)),
            direction: "ltr",
            "with-header": false,
            size: "250px",
            "custom-class": "mobile-nav-drawer"
          }, {
            default: _withCtx(() => [
              (currentModule.value === 'p2p')
                ? (_openBlock(), _createBlock(MainMenu, {
                    key: 0,
                    collapse: false,
                    "show-golf-module": hasGolfAccess.value,
                    onSwitchModule: switchToModuleAndClose
                  }, null, 8, ["show-golf-module"]))
                : (currentModule.value === 'ev')
                  ? (_openBlock(), _createBlock(EVMenu, {
                      key: 1,
                      collapse: false,
                      "show-golf-module": hasGolfAccess.value,
                      onSwitchModule: switchToModuleAndClose
                    }, null, 8, ["show-golf-module"]))
                  : (currentModule.value === 'golf')
                    ? (_openBlock(), _createBlock(GolfMenu, {
                        key: 2,
                        collapse: false,
                        "show-golf-module": hasGolfAccess.value,
                        onSwitchModule: switchToModuleAndClose
                      }, null, 8, ["show-golf-module"]))
                    : _createCommentVNode("", true)
            ]),
            _: 1
          }, 8, ["modelValue"]),
          _createVNode(_component_el_container, {
            class: _normalizeClass(["body-container", {
          'golf-bg': currentModule.value === 'golf',
          'ev-module-container': currentModule.value === 'ev',
        }]),
            style: _normalizeStyle({ height: containerHeight.value })
          }, {
            default: _withCtx(() => [
              (currentModule.value === 'golf')
                ? (_openBlock(), _createElementBlock("div", _hoisted_14, [
                    _createVNode(_component_el_main, { class: "golf-module-bg" }, {
                      default: _withCtx(() => [
                        _renderSlot(_ctx.$slots, "default")
                      ]),
                      _: 3
                    })
                  ]))
                : (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                    (
              currentModule.value === 'p2p' && !isStatisticsViewer.value && !isEvCarOwner.value
            )
                      ? (_openBlock(), _createBlock(MainMenu, {
                          key: 0,
                          collapse: collapse.value,
                          "show-golf-module": hasGolfAccess.value,
                          onSwitchModule: switchToModule,
                          class: "desktop-sidebar"
                        }, null, 8, ["collapse", "show-golf-module"]))
                      : (
              currentModule.value === 'ev' && !isStatisticsViewer.value && !isEvCarOwner.value
            )
                        ? (_openBlock(), _createBlock(EVMenu, {
                            key: 1,
                            collapse: collapse.value,
                            "show-golf-module": hasGolfAccess.value,
                            onSwitchModule: switchToModule,
                            class: "desktop-sidebar"
                          }, null, 8, ["collapse", "show-golf-module"]))
                        : _createCommentVNode("", true),
                    _createVNode(_component_el_main, {
                      class: _normalizeClass({ 'ev-main': currentModule.value === 'ev' })
                    }, {
                      default: _withCtx(() => [
                        _renderSlot(_ctx.$slots, "default")
                      ]),
                      _: 3
                    }, 8, ["class"])
                  ], 64))
            ]),
            _: 3
          }, 8, ["class", "style"]),
          (!isStatisticsViewer.value && !isEvCarOwner.value)
            ? (_openBlock(), _createBlock(_component_el_footer, { key: 1 }, {
                default: _withCtx(() => [
                  _createTextVNode("© " + _toDisplayString(currentYear.value), 1)
                ]),
                _: 1
              }))
            : _createCommentVNode("", true)
        ]),
        _: 3
      }, 8, ["style"])
    ]),
    _: 3
  }, 8, ["direction", "class", "style"]))
}
}

})